import { PROCESS_SAMPLE_DURATION_SECONDS } from '@electron/shared';
import { useState } from 'react';
import { toast } from 'sonner';

import { ModalsManager } from '@/components/modals/modalsManager';
import { uploadLogToS3 } from '@/features/app-logs/app-logs.utils';
import { electronApis } from '@/features/electron-api';
import { FeedbackModal } from '@/features/feedback/feedback';
import { Feedback } from '@/features/feedback/feedback.types';

export const useProcessSampleConfirmationModal = () => {
  const [isCapturing, setIsCapturing] = useState(false);

  const captureAndSubmitFeedback = async () => {
    setIsCapturing(true);
    try {
      const {
        sampleData,
        snapshotUrl,
        appLogUrl,
        crashLogUrl,
        contextKitCrashLogUrl,
      } = await captureAndUploadProcessSample();

      let feedbackText = `Performance Diagnostic Snapshot

Duration: ${sampleData.duration}s
Timestamp: ${sampleData.timestamp}
Process Sample: ${snapshotUrl}
`;

      // Add log URLs if available
      if (appLogUrl) {
        feedbackText += `App Log: ${appLogUrl}\n`;
      }
      if (crashLogUrl) {
        feedbackText += `App Crash Log: ${crashLogUrl}\n`;
      }
      if (contextKitCrashLogUrl) {
        feedbackText += `ContextKit Crash Log: ${contextKitCrashLogUrl}\n`;
      }

      feedbackText += `
Please describe the performance issue you were experiencing:

`;

      // Open feedback modal to let the user submit the feedback
      ModalsManager.show(FeedbackModal, {
        initialFeedbackType: Feedback.Other,
        initialFeedback: feedbackText,
      });
    } catch (error) {
      console.error('Failed to capture or send diagnostic snapshot:', error);
    } finally {
      setIsCapturing(false);
    }
  };

  return {
    isCapturing,
    onCaptureSampleClick: captureAndSubmitFeedback,
  };
};

async function captureAndUploadProcessSample() {
  const toastId = toast.info(
    `Creating diagnostic snapshot to help us troubleshoot performance issues. This will take ${PROCESS_SAMPLE_DURATION_SECONDS} seconds.`,
    { description: '', duration: (PROCESS_SAMPLE_DURATION_SECONDS + 1) * 1000 }
  );

  try {
    const sampleData = await electronApis.system.captureProcessSample();
    console.info('Diagnostic snapshot captured:', {
      filePath: sampleData.filePath,
      fileName: sampleData.fileName,
      duration: sampleData.duration,
      timestamp: sampleData.timestamp,
      contentLength: sampleData.fileContent.length,
    });

    toast.loading('Uploading diagnostic snapshot and crash logs...', {
      id: toastId,
    });

    // Remove .txt extension from fileName since uploadLogToS3 adds it
    const fileNameWithoutExt = sampleData.fileName.replace(/\.txt$/, '');
    const snapshotUrl = await uploadLogToS3(
      sampleData.fileContent,
      fileNameWithoutExt
    );

    console.info('Diagnostic snapshot uploaded to:', snapshotUrl);

    // Fetch and upload the latest logs in parallel
    const [appLogResult, crashLogResult, contextKitCrashLogResult] =
      await Promise.allSettled([
        (async () => {
          const latestLogFile =
            await electronApis.system.getLatestLogFileContent();
          if (latestLogFile) {
            const appLogFileNameWithoutExt = latestLogFile.fileName.replace(
              /\.(log|txt)$/,
              ''
            );
            const url = await uploadLogToS3(
              latestLogFile.content,
              appLogFileNameWithoutExt
            );
            console.info('App log uploaded to:', url);
            return url;
          }
          return undefined;
        })(),
        (async () => {
          const latestCrashLog =
            await electronApis.system.getLatestCrashLogContent();
          if (latestCrashLog) {
            const crashLogFileNameWithoutExt = latestCrashLog.fileName.replace(
              /\.(crash|ips|txt)$/,
              ''
            );
            const url = await uploadLogToS3(
              latestCrashLog.content,
              crashLogFileNameWithoutExt
            );
            console.info('App crash log uploaded to:', url);
            return url;
          }
          return undefined;
        })(),
        (async () => {
          const latestContextKitCrashLog =
            await electronApis.system.getLatestContextKitCliCrashLogContent();
          if (latestContextKitCrashLog) {
            const contextKitCrashLogFileNameWithoutExt =
              latestContextKitCrashLog.fileName.replace(
                /\.(crash|ips|txt)$/,
                ''
              );
            const url = await uploadLogToS3(
              latestContextKitCrashLog.content,
              contextKitCrashLogFileNameWithoutExt
            );
            console.info('ContextKit crash log uploaded to:', url);
            return url;
          }
          return undefined;
        })(),
      ]);

    const appLogUrl =
      appLogResult.status === 'fulfilled' ? appLogResult.value : undefined;
    const crashLogUrl =
      crashLogResult.status === 'fulfilled' ? crashLogResult.value : undefined;
    const contextKitCrashLogUrl =
      contextKitCrashLogResult.status === 'fulfilled'
        ? contextKitCrashLogResult.value
        : undefined;

    if (appLogResult.status === 'rejected') {
      console.warn('Failed to fetch or upload app log:', appLogResult.reason);
    }
    if (crashLogResult.status === 'rejected') {
      console.warn(
        'Failed to fetch or upload app crash log:',
        crashLogResult.reason
      );
    }
    if (contextKitCrashLogResult.status === 'rejected') {
      console.warn(
        'Failed to fetch or upload ContextKit crash log:',
        contextKitCrashLogResult.reason
      );
    }

    toast.success(
      'All set! Please use the dialog to submit your report with more details.',
      {
        id: toastId,
        duration: 4000,
      }
    );

    return {
      sampleData,
      snapshotUrl,
      appLogUrl,
      crashLogUrl,
      contextKitCrashLogUrl,
    };
  } catch (error) {
    console.error('Failed to capture diagnostic snapshot:', error);
    toast.error(
      'Failed to create diagnostic snapshot. Please try again or contact support.',
      {
        description: error instanceof Error ? error.message : String(error),
        id: toastId,
        duration: 4000,
      }
    );
    throw error;
  }
}
